import { FC } from "react"
import { makeStyles } from "@material-ui/core"
import { colors as themeColors } from "../../../utils/theme"

export type DBTScore = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

type DBTScoreVariant = "A" | "B"

type DBTScoreScale = "intensity-6" | "intensity-8" | "emotions-6" | "meals-7"

type ColorStop = {
  background: string
  text: string
  border?: string
}

interface DBTScoreIconProps {
  score: DBTScore
  variant?: DBTScoreVariant
  scale?: DBTScoreScale
  paletteOverride?: readonly ColorStop[]
}

const { intensity, meals, text, borders } = themeColors.dbt

const SCALE_LIBRARY: Record<DBTScoreScale, readonly ColorStop[]> = {
  "emotions-6": [
    { background: intensity[0], text: text.neutral, border: "transparent" },
    {
      background: intensity[1],
      text: text.lowIntensity,
      border: borders.lowIntensity,
    },
    {
      background: intensity[2],
      text: themeColors.neutral.white,
      border: borders.mildIntensity,
    },
    {
      background: intensity[4],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[6],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[7],
      text: themeColors.neutral.white,
      border: "transparent",
    },
  ],
  "intensity-6": [
    { background: intensity[0], text: text.neutral, border: "transparent" },
    {
      background: intensity[1],
      text: text.lowIntensity,
      border: borders.lowIntensity,
    },
    {
      background: intensity[2],
      text: themeColors.neutral.white,
      border: borders.mildIntensity,
    },
    {
      background: intensity[4],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[6],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[7],
      text: themeColors.neutral.white,
      border: "transparent",
    },
  ],
  "intensity-8": [
    { background: intensity[0], text: text.neutral, border: "transparent" },
    {
      background: intensity[1],
      text: text.lowIntensity,
      border: borders.lowIntensity,
    },
    {
      background: intensity[2],
      text: themeColors.neutral.white,
      border: borders.mildIntensity,
    },
    {
      background: intensity[3],
      text: themeColors.neutral.white,
      border: borders.moderateIntensity,
    },
    {
      background: intensity[4],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[5],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[6],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[7],
      text: themeColors.neutral.white,
      border: "transparent",
    },
  ],
  "meals-7": [
    {
      background: intensity[7],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: meals.possibleConcern,
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: meals.somewhatLow,
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[1],
      text: text.normalMeal,
      border: borders.normalMeal,
    },
    {
      background: intensity[3],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[6],
      text: themeColors.neutral.white,
      border: "transparent",
    },
    {
      background: intensity[7],
      text: themeColors.neutral.white,
      border: "transparent",
    },
  ],
}

const clampScore = (value: number, palette: readonly ColorStop[]): number => {
  if (palette.length === 0) {
    return 0
  }

  if (value < 0) {
    return 0
  }

  if (value >= palette.length) {
    return palette.length - 1
  }

  return value
}

/**
 * DBTScoreIcon renders the pill-style numbers used in the diary cards.
 * Consumers can switch between the Meals (7pt) and Intensity (5pt/8pt) ramps
 * via `scale` or pass a custom palette for experimentation.
 */
const DBTScoreIcon: FC<DBTScoreIconProps> = ({
  score,
  variant = "A",
  scale = "intensity-6",
  paletteOverride,
}) => {
  const classes = useStyles()
  const palette = paletteOverride ?? SCALE_LIBRARY[scale]
  const resolvedIndex = clampScore(score, palette)
  const colors = palette[resolvedIndex] ?? SCALE_LIBRARY["intensity-6"][0]
  const borderWidth = colors.border === "transparent" ? 0 : 2
  return (
    <div
      className={classes.container}
      data-variant={variant}
      data-scale={scale}
      style={{
        backgroundColor: colors.background,
        borderColor: colors.border ?? "transparent",
        borderWidth,
      }}
    >
      <span className={classes.scoreText} style={{ color: colors.text }}>
        {score}
      </span>
    </div>
  )
}

const useStyles = makeStyles({
  container: {
    width: 34,
    height: 34,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "solid",
    boxSizing: "border-box",
  },
  scoreText: {
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "20px",
    textAlign: "center",
  },
})

export default DBTScoreIcon
